import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

interface Props {
  value: string;
  placeholder?: string;
  className?: string;
  focusOnMount?: boolean;
  selectOnMount?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEnterPressed?: (e?: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  debounceTime?: number;
  form?: string;
  maxCharCount?: number;
  isAllowedChar?: (char: string) => boolean;
  'aria-invalid'?: boolean;
}

function Index({
  value,
  className,
  focusOnMount,
  selectOnMount,
  onChange,
  onFocus,
  onBlur,
  onEnterPressed,
  onKeyDown,
  placeholder,
  form,
  debounceTime = 300,
  maxCharCount,
  isAllowedChar,
  'aria-invalid': ariaInvalid,
}: Props) {
  const [content, setContent] = useState(value);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Where the caret has to go once the incoming value has been painted, or
  // null when the browser's own placement is right.
  //
  // A value that arrives while the buyer is typing is normally the store
  // handing back what they typed in the template's canonical form — a forced
  // email domain, a phone mask (see applyValueFormat). Binding the input to
  // that longer string moves the caret to its end, so the next keystrokes
  // landed after the appended domain: "vipul" became "v@whitecase.comipul".
  const caretToRestore = React.useRef<number | null>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (input && value !== content && document.activeElement === input) {
      const caret = input.selectionStart ?? content.length;
      // The formatter only ever appends to what has been typed so far, so a
      // value that still opens with the text before the caret leaves that
      // position meaningful and the buyer keeps typing where they were.
      // Anything else — a mask re-pouring the digits — has no position to
      // hold on to, and the end is where typing continues.
      caretToRestore.current = value.startsWith(content.slice(0, caret)) ? caret : value.length;
    }
    setContent(value);
  }, [value]);

  // After the paint, so it overrides the caret the value assignment left.
  useLayoutEffect(() => {
    const caret = caretToRestore.current;
    if (caret === null) {
      return;
    }
    caretToRestore.current = null;
    inputRef.current?.setSelectionRange(caret, caret);
  }, [content]);
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
    if (selectOnMount) {
      inputRef.current?.select();
    }
  }, [focusOnMount, selectOnMount]);

  const onContentUpdate = useCallback(debounce(onChange, debounceTime, { trailing: true }), [onChange, debounceTime]);

  function onTextChange(e: React.ChangeEvent) {
    const { value: typed } = e.target as HTMLInputElement;
    let next = typed;
    // 0 is a real cap. Clamp only an edit that grew the value, so backspacing
    // an already over-long string deletes one character instead of keeping
    // the first N.
    if (maxCharCount != null && next.length > maxCharCount && next.length > content.length) {
      next = next.slice(0, maxCharCount);
    }

    if (isAllowedChar) {
      for (let i = 0; i < next.length; i += 1) {
        if (!isAllowedChar(next[i])) {
          next = next.slice(0, i) + next.slice(i + 1);
          i -= 1;
        }
      }
    }

    setContent(next);
    onContentUpdate(next);
  }

  function onKeyDownHandler(e: React.KeyboardEvent<HTMLInputElement>) {
    if (onEnterPressed && e.key === 'Enter') {
      onEnterPressed();
    }
    onKeyDown?.(e);
  }

  return (
    <input
      ref={inputRef}
      placeholder={placeholder}
      type="text"
      value={content}
      className={className}
      onChange={onTextChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDownHandler}
      form={form}
      aria-invalid={ariaInvalid}
    />
  );
}

export default React.memo(Index);
